import { supabase } from '../supabase/client';

export interface AblageVorschlag {
  id: string;
  anhang_id: string;
  objekt_id: string | null;
  ziel_pfad: string | null;
  dateiname: string;
  regel_id: string | null;
  confidence: number;
  status: 'offen' | 'freigegeben' | 'abgelegt' | 'abgelehnt' | 'fehler';
  dokument_id: string | null;
  gdrive_file_id: string | null;
}

export function driveDateiUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/view`;
}

export async function ladeVorschlaegeFuerEmail(emailId: string): Promise<Record<string, AblageVorschlag>> {
  const { data } = await supabase.from('dokument_ablage_vorschlaege')
    .select('id, anhang_id, objekt_id, ziel_pfad, dateiname, regel_id, confidence, status, dokument_id, gdrive_file_id')
    .eq('email_id', emailId);
  const byAnhang: Record<string, AblageVorschlag> = {};
  for (const v of (data || []) as AblageVorschlag[]) byAnhang[v.anhang_id] = v;
  return byAnhang;
}

export async function ladeOrdnerpfade(objektId: string): Promise<string[]> {
  const { data } = await supabase.from('gdrive_ordner')
    .select('pfad').eq('objekt_id', objektId);
  const set = new Set<string>();
  for (const r of (data || []) as { pfad: string | null }[]) if (r.pfad) set.add(r.pfad);
  return [...set].sort();
}

export async function legeAnhangAb(input: {
  anhangId: string; objektId: string; zielPfad: string; dateiname: string; kategorie?: string | null;
}): Promise<{ gdrive_file_id: string; dokument_id: string; ordner_id: string }> {
  const { data, error } = await supabase.functions.invoke('gdrive', {
    body: {
      action: 'ablegen',
      anhangId: input.anhangId,
      objektId: input.objektId,
      zielPfad: input.zielPfad,
      dateiname: input.dateiname,
      kategorie: input.kategorie ?? null,
    },
  });
  if (error) {
    let msg = error.message || 'Ablage fehlgeschlagen';
    const resp: any = (error as any).context;
    if (resp && typeof resp.json === 'function') {
      try { const b = await resp.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
    }
    throw new Error(msg);
  }
  return data as { gdrive_file_id: string; dokument_id: string; ordner_id: string };
}

export async function markiereVorschlag(
  id: string,
  patch: { status?: AblageVorschlag['status']; dokument_id?: string | null; gdrive_file_id?: string | null; entschieden_von?: string | null },
): Promise<void> {
  const { error } = await supabase.from('dokument_ablage_vorschlaege')
    .update({ ...patch, entschieden_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ── Dokumente E3: Ablage eines Vorschlags (Karte und Massenablage) ───────────

export type AblageErgebnis =
  | { art: 'abgelegt'; gdriveFileId: string | null; dokumentId: string | null; statusFehler: boolean }
  | { art: 'schon-abgelegt' };

/**
 * Legt einen Vorschlag in Drive ab und vermerkt den Status. Wie in der E-Mail-Karte: liegt die Datei schon
 * (uq_dokumente_quelle_anhang), gilt der Vorschlag als abgelegt; scheitert nur der Statusvermerk, bleibt er abgelegt.
 */
export async function legeVorschlagAb(input: {
  vorschlagId: string; anhangId: string; objektId: string; zielPfad: string; dateiname: string; userId: string | null;
}): Promise<AblageErgebnis> {
  let r: { gdrive_file_id: string; dokument_id: string; ordner_id: string };
  try {
    r = await legeAnhangAb({ anhangId: input.anhangId, objektId: input.objektId, zielPfad: input.zielPfad.trim(), dateiname: input.dateiname.trim() });
  } catch (e) {
    const msg = e instanceof Error ? e.message : '';
    if (/uq_dokumente_quelle_anhang|duplicate key/i.test(msg)) {
      await markiereVorschlag(input.vorschlagId, { status: 'abgelegt', entschieden_von: input.userId }).catch(() => undefined);
      return { art: 'schon-abgelegt' };
    }
    throw e;
  }
  let statusFehler = false;
  try {
    await markiereVorschlag(input.vorschlagId, { status: 'abgelegt', dokument_id: r.dokument_id, gdrive_file_id: r.gdrive_file_id, entschieden_von: input.userId });
  } catch { statusFehler = true; }
  return { art: 'abgelegt', gdriveFileId: r.gdrive_file_id, dokumentId: r.dokument_id, statusFehler };
}

// Vor dem Ablegen geänderte Felder (Objekt, Ordner, Name) am Vorschlag merken.
export async function aktualisiereVorschlag(id: string, patch: { objekt_id?: string | null; ziel_pfad?: string | null; dateiname?: string }): Promise<void> {
  const { error } = await supabase.from('dokument_ablage_vorschlaege').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw new Error(error.message);
}
